/**
 * Resolves an event's linked venue location.
 *
 * Per ADR-098, Event documents only carry a `venueLocationId` reference.
 * The venue's display strings (label, address, city, state, country) and
 * coordinates live on the linked `eventLocations` document. Consumers
 * that need to display venue info or bias a sub-picker by the event's
 * geographic context use this hook.
 *
 * Returns `null` when the event has no venue location set, or while the
 * locations subscription is still loading.
 */

import { useMemo } from 'react';
import type { Event, EventLocation } from '@event-planner/shared';
import { useEventLocations } from './useEventLocations';

export function useEventVenue(event: Event | null | undefined): {
  venue: EventLocation | null;
  isLoading: boolean;
} {
  const eventId = event?.id ?? '';
  const { locations, isLoading } = useEventLocations(eventId);

  const venue = useMemo(() => {
    if (!event?.venueLocationId) return null;
    return locations.find((l) => l.id === event.venueLocationId) ?? null;
  }, [event?.venueLocationId, locations]);

  return { venue, isLoading };
}
